import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentArrowDownIcon, 
  ClipboardDocumentIcon,
  ChartBarIcon,
  CurrencyDollarIcon 
} from '@heroicons/react/24/outline';

// Mock data for Tokenomics Metrics
const tokenomicsData = [
  {
    id: 1,
    metric: 'Circulating Supply',
    value: '847.2M',
    benchmark: '< 1B tokens',
    status: 'green',
    unit: 'tokens'
  },
  {
    id: 2,
    metric: 'Total Supply',
    value: '1.0B',
    benchmark: '< 10B tokens',
    status: 'green',
    unit: 'tokens'
  },
  {
    id: 3,
    metric: 'Max Supply',
    value: '1.0B',
    benchmark: 'Fixed supply',
    status: 'green',
    unit: 'tokens'
  },
  {
    id: 4,
    metric: 'Inflation Rate / Emission Schedule',
    value: '5.8%',
    benchmark: '< 5% annually',
    status: 'amber',
    unit: '% per year'
  },
  {
    id: 5,
    metric: 'Token Unlock Schedule',
    value: '4 months',
    benchmark: '> 6 months',
    status: 'red',
    unit: 'months'
  },
  {
    id: 6,
    metric: 'Foundation/Team Allocation %',
    value: '18%',
    benchmark: '< 20%',
    status: 'green',
    unit: '%'
  },
  {
    id: 7,
    metric: 'VC Allocation %',
    value: '32%',
    benchmark: '< 30%',
    status: 'amber',
    unit: '%'
  },
  {
    id: 8,
    metric: 'Community Incentives %',
    value: '35%',
    benchmark: '> 30%',
    status: 'green',
    unit: '%'
  },
  {
    id: 9,
    metric: 'Token Utility',
    value: 'Medium',
    benchmark: 'Multi-purpose',
    status: 'amber',
    unit: 'qualitative'
  },
  {
    id: 10,
    metric: 'On-Chain Usage/Velocity',
    value: '0.45',
    benchmark: '0.3 - 0.6',
    status: 'green',
    unit: 'ratio'
  },
  {
    id: 11,
    metric: 'Burn Mechanism',
    value: 'Inactive',
    benchmark: 'Implemented',
    status: 'red',
    unit: 'boolean'
  },
  {
    id: 12,
    metric: 'Bridged Supply / Multichain Exposure',
    value: '28%',
    benchmark: '< 25%',
    status: 'amber',
    unit: '%'
  },
  {
    id: 13,
    metric: 'Supply Centralization Score',
    value: '72/100',
    benchmark: '> 60/100',
    status: 'green',
    unit: 'score'
  }
];

// Mock data for Liquidity Metrics
const liquidityData = [
  {
    id: 1,
    metric: 'Daily Trading Volume (30d avg)',
    value: '$45.2M',
    benchmark: '> $10M',
    status: 'green',
    unit: 'USD'
  },
  {
    id: 2,
    metric: 'Slippage at $100k Trade Size',
    value: '2.8%',
    benchmark: '< 2%',
    status: 'red',
    unit: '%'
  },
  {
    id: 3,
    metric: 'Depth at 1% Bid/Ask on CEX',
    value: '$750K',
    benchmark: '> $1M',
    status: 'amber',
    unit: 'USD'
  },
  {
    id: 4,
    metric: 'Depth at 1% Bid/Ask on DEX',
    value: '$350K',
    benchmark: '> $500K',
    status: 'red',
    unit: 'USD'
  },
  {
    id: 5,
    metric: '# of Active Exchanges',
    value: '6',
    benchmark: '> 8',
    status: 'amber',
    unit: 'count'
  },
  {
    id: 6,
    metric: 'Top Exchange Coverage',
    value: '85%',
    benchmark: '> 70%',
    status: 'green',
    unit: '%'
  },
  {
    id: 7,
    metric: 'Market Maker Presence',
    value: '2 firms',
    benchmark: '> 3 firms',
    status: 'red',
    unit: 'count'
  },
  {
    id: 8,
    metric: 'Spread (30d average)',
    value: '0.68%',
    benchmark: '< 0.5%',
    status: 'amber',
    unit: '%'
  },
  {
    id: 9,
    metric: 'Volatility (30d)',
    value: '6.8%',
    benchmark: '< 5%',
    status: 'red',
    unit: '%'
  },
  {
    id: 10,
    metric: 'Real Volume Ratio',
    value: '58%',
    benchmark: '> 70%',
    status: 'red',
    unit: '%'
  },
  {
    id: 11,
    metric: '% of Supply on Exchanges',
    value: '45%',
    benchmark: '< 40%',
    status: 'amber',
    unit: '%'
  },
  {
    id: 12,
    metric: 'Stablecoin Pair Liquidity',
    value: '$3.2M',
    benchmark: '> $5M',
    status: 'amber',
    unit: 'USD'
  }
];

// RAG Status Component
const RAGIndicator = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'green':
        return {
          bg: 'bg-accent-success/20',
          border: 'border-accent-success/50',
          text: 'text-accent-success',
          label: 'Good'
        };
      case 'amber':
        return {
          bg: 'bg-accent-warning/20',
          border: 'border-accent-warning/50',
          text: 'text-accent-warning',
          label: 'Caution'
        };
      case 'red':
        return {
          bg: 'bg-accent-danger/20',
          border: 'border-accent-danger/50',
          text: 'text-accent-danger',
          label: 'Risk'
        };
      default:
        return {
          bg: 'bg-cream-100/20',
          border: 'border-cream-100/50',
          text: 'text-cream-100',
          label: 'N/A'
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.border} ${config.text}`}>
      <span className={`w-2 h-2 rounded-full mr-2 ${status === 'green' ? 'bg-accent-success' : status === 'amber' ? 'bg-accent-warning' : status === 'red' ? 'bg-accent-danger' : 'bg-cream-100'}`}></span>
      {config.label}
    </span>
  );
};

// Metric Row Component
const MetricRow = ({ metric, index }) => {
  return (
    <motion.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="border-b border-white/10 hover:bg-white/5 transition-all duration-200"
    >
      <td className="px-6 py-4 text-cream-100 font-medium text-sm">
        {metric.metric}
      </td>
      <td className="px-6 py-4 text-cream-200 text-sm font-mono">
        {metric.value}
      </td>
      <td className="px-6 py-4 text-cream-300 text-sm">
        {metric.benchmark}
      </td>
      <td className="px-6 py-4">
        <RAGIndicator status={metric.status} />
      </td>
    </motion.tr>
  );
};

// Export functionality
const exportToCSV = (data, fileName) => {
  const headers = ['Metric', 'Value', 'Industry Benchmark', 'Status'];
  const csvContent = [
    headers.join(','),
    ...data.map(row => [
      `"${row.metric}"`,
      `"${row.value}"`,
      `"${row.benchmark}"`,
      `"${row.status}"`
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${fileName}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToPDF = async (data, fileName) => {
  // Simple HTML to PDF conversion using browser print
  const printWindow = window.open('', '', 'height=600,width=800');
  
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${fileName}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
        th { background-color: #f2f2f2; font-weight: bold; }
        .status-green { color: #10B981; }
        .status-amber { color: #F59E0B; }
        .status-red { color: #EF4444; }
        h1 { color: #0B0E1C; margin-bottom: 10px; }
        .export-date { color: #666; font-size: 14px; margin-bottom: 20px; }
      </style>
    </head>
    <body>
      <h1>${fileName}</h1>
      <div class="export-date">Generated on: ${new Date().toLocaleDateString()}</div>
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Value</th>
            <th>Industry Benchmark</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          ${data.map(row => `
            <tr>
              <td>${row.metric}</td>
              <td>${row.value}</td>
              <td>${row.benchmark}</td>
              <td class="status-${row.status}">${row.status.charAt(0).toUpperCase() + row.status.slice(1)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </body>
    </html>
  `;
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.focus();
  
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};

const TokenScorecard = () => {
  const [activeTab, setActiveTab] = useState('tokenomics');

  const currentData = activeTab === 'tokenomics' ? tokenomicsData : liquidityData;
  const currentTitle = activeTab === 'tokenomics' ? 'Tokenomics Metrics' : 'Liquidity Metrics';

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass-card p-8"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-2">
              Token Scorecard
            </h2>
            <p className="text-cream-200">
              Comprehensive analysis of token performance, liquidity metrics, and tokenomics health.
            </p>
          </div>
          
          {/* Export Buttons */}
          <div className="flex space-x-3 mt-4 lg:mt-0">
            <button
              onClick={() => exportToCSV(currentData, `${currentTitle.replace(' ', '_')}_Export`)}
              className="flex items-center space-x-2 px-4 py-2 bg-accent-blue/20 border border-accent-blue/30 text-accent-blue rounded-lg hover:bg-accent-blue/30 transition-all duration-200"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              <span className="text-sm font-medium">CSV</span>
            </button>
            <button
              onClick={() => exportToPDF(currentData, `${currentTitle.replace(' ', '_')}_Export`)}
              className="flex items-center space-x-2 px-4 py-2 bg-accent-gold/20 border border-accent-gold/30 text-accent-gold rounded-lg hover:bg-accent-gold/30 transition-all duration-200"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              <span className="text-sm font-medium">PDF</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-white/5 p-1 rounded-xl">
          <button
            onClick={() => setActiveTab('tokenomics')}
            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-all duration-300 ${
              activeTab === 'tokenomics'
                ? 'bg-accent-blue text-white shadow-lg'
                : 'text-cream-300 hover:text-cream-100 hover:bg-white/5'
            }`}
          >
            <ChartBarIcon className="h-5 w-5" />
            <span className="font-medium">Tokenomics Metrics</span>
            <span className="bg-white/20 text-xs px-2 py-1 rounded-full">13</span>
          </button>
          <button
            onClick={() => setActiveTab('liquidity')}
            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-all duration-300 ${
              activeTab === 'liquidity'
                ? 'bg-accent-blue text-white shadow-lg'
                : 'text-cream-300 hover:text-cream-100 hover:bg-white/5'
            }`}
          >
            <CurrencyDollarIcon className="h-5 w-5" />
            <span className="font-medium">Liquidity Metrics</span>
            <span className="bg-white/20 text-xs px-2 py-1 rounded-full">12</span>
          </button>
        </div>

        {/* Metrics Table */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="overflow-x-auto"
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/20">
                <th className="px-6 py-4 text-left text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-6 py-4 text-left text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-4 text-left text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Industry Benchmark
                </th>
                <th className="px-6 py-4 text-left text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((metric, index) => (
                <MetricRow key={metric.id} metric={metric} index={index} />
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Summary Stats */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-12 border-t border-white/10 pt-8"
        >
          <h3 className="text-heading-md font-serif font-bold text-cream-100 mb-6 text-center">
            {currentTitle} Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-accent-success/10 border border-accent-success/20 rounded-xl p-6 hover:bg-accent-success/15 transition-all duration-200"
            >
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-4 h-4 bg-accent-success rounded-full shadow-lg"></div>
                <span className="text-accent-success font-semibold text-sm uppercase tracking-wide">Good</span>
              </div>
              <div className="text-3xl font-bold text-cream-100 mb-1">
                {currentData.filter(m => m.status === 'green').length}
              </div>
              <div className="text-cream-300 text-sm">metrics performing well</div>
              <div className="mt-2 text-xs text-accent-success">
                {Math.round((currentData.filter(m => m.status === 'green').length / currentData.length) * 100)}% of total
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-accent-warning/10 border border-accent-warning/20 rounded-xl p-6 hover:bg-accent-warning/15 transition-all duration-200"
            >
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-4 h-4 bg-accent-warning rounded-full shadow-lg"></div>
                <span className="text-accent-warning font-semibold text-sm uppercase tracking-wide">Caution</span>
              </div>
              <div className="text-3xl font-bold text-cream-100 mb-1">
                {currentData.filter(m => m.status === 'amber').length}
              </div>
              <div className="text-cream-300 text-sm">metrics need attention</div>
              <div className="mt-2 text-xs text-accent-warning">
                {Math.round((currentData.filter(m => m.status === 'amber').length / currentData.length) * 100)}% of total
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-accent-danger/10 border border-accent-danger/20 rounded-xl p-6 hover:bg-accent-danger/15 transition-all duration-200"
            >
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-4 h-4 bg-accent-danger rounded-full shadow-lg"></div>
                <span className="text-accent-danger font-semibold text-sm uppercase tracking-wide">Risk</span>
              </div>
              <div className="text-3xl font-bold text-cream-100 mb-1">
                {currentData.filter(m => m.status === 'red').length}
              </div>
              <div className="text-cream-300 text-sm">metrics at risk</div>
              <div className="mt-2 text-xs text-accent-danger">
                {Math.round((currentData.filter(m => m.status === 'red').length / currentData.length) * 100)}% of total
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default TokenScorecard;